import React, { useEffect, useState, useRef } from "react";
import Markdown from "react-markdown";
import { styled } from "styled-components";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { Highlight, themes } from "prism-react-renderer";
import { useTheme } from 'styled-components';
import CopyIcon from "assets/CopyIcon";
import CheckIcon from "assets/CheckIcon";
import DownloadIcon from "assets/DownloadIcon";
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const MarkdownContainer = styled("div")`
  // Fix for table margin spacing

  .content-transition {
    transition: max-height 0.3s ease-in-out;
    overflow: hidden;
  }
  
  .markdown pre {
    overflow-x: auto;
    white-space: pre-wrap;
    word-wrap: break-word;
    overflow-wrap: break-word;
    max-width: 100%;
  }

  // The theme cannot affect this page
  p,
  li,
  span,
  strong,
  answer,
  a,
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    color: ${({ theme }) => theme.colors.textColor};
    word-wrap: break-word;
    overflow-wrap: break-word;
    max-width: 100%;
  }

  legend {
    font-size: ${({ theme }) => theme.font.large};
  }

  pre {
    span {
      color: inherit;
    }
  }

  .markdown {
    p {
      margin: 0.25rem 0;
      color: ${({ theme }) => theme.colors.textColor} !important;
      word-wrap: break-word;
      overflow-wrap: break-word;
      max-width: 100%;
    }

    ol,
    ul,
    menu {
      list-style: revert;
      margin: revert;
      padding: revert;
      word-wrap: break-word;
      overflow-wrap: break-word;
      max-width: 100%;
    }

    p strong {
      line-height: 1.8rem;
    }

    li {
      margin: 0.75rem 0;
      word-wrap: break-word;
      overflow-wrap: break-word;
      max-width: 100%;
    }

    h2 {
      font-size: 1.2rem !important;
      font-weight: 600 !important;
    }

    a {
      color: #3b82f6; // Link color
      text-decoration: underline;
      transition: all 0.2s ease;
      position: relative;

      &:hover {
        color: #60a5fa;
        text-decoration: none;
      }

      &:focus {
        outline: 2px solid #60a5fa;
        outline-offset: 2px;
      }
    }
  }

  .prism-code {
    padding: 0.75rem;
    word-wrap: break-word;
    overflow-wrap: break-word;
    max-width: 100%;
  }

  .markdown > pre {
    margin: 0.5rem 0;
    overflow-x: auto;
    border-radius: 0.375rem;
    background-color: rgb(39, 40, 34);
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
      Liberation Mono, Courier New, monospace;
    font-size: 0.75rem;
    line-height: 1rem;
    --tw-text-opacity: 1;
    color: rgb(255 255 255 / var(--tw-text-opacity, 1));
    white-space: pre-wrap;
    word-wrap: break-word;
    overflow-wrap: break-word;
    max-width: 100%;
  }

  .markdown > pre {
    box-shadow: 1.5px 3px 3px hsl(0deg 0% 0% / 0.45);
    border-radius: 0.375rem;
  }

  .markdown {
    .code-block-container {
      border-radius: 0.375rem;
      overflow: hidden;
      max-width: 100%;

      span {
        color: rgb(255 255 255 / var(--tw-text-opacity, 1));
        word-wrap: break-word;
        overflow-wrap: break-word;
      }

      .code-block-header {
        border-top-left-radius: 0.375rem;
        border-top-right-radius: 0.375rem;
        
        svg {
          height: 18px;
        }
      }
    }
  }

  .copy-btn {
    position: absolute;
    top: 5px;
    right: 5px;
    background-color: #007bff;
    color: white;
    border: none;
    border-radius: 5px;
    padding: 0.5rem;
    cursor: pointer;
    font-size: 0.75rem;
  }

  .copy-feedback {
    position: absolute;
    top: 5px;
    right: 70px;
    font-size: 0.75rem;
    color: #28a745;
  }

  table {
    border-collapse: collapse;
    width: 100%;
    table-layout: fixed;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }

  th,
  td {
    border: 1px solid #ddd;
    padding: 8px;
    text-align: left;
    word-wrap: break-word;
    overflow-wrap: break-word;
    max-width: 100%;
  }

  th {
    background-color: #333;
    color: white;
  }

  tr:nth-child(even) {
    background-color: #2d2d2d;
    color: #ffffff;
  }

  th strong {
    color: ${({ theme }) => theme.name === 'light-theme' ? theme.colors.textColorOpposite : theme.colors.textColor};
  }

  tr:nth-child(even) strong {
    color: ${({ theme }) => theme.name === 'light-theme' ? theme.colors.textColorOpposite : theme.colors.textColor};
  }
`;

// Styled container for each step section with accordion functionality
const StepSection = styled.div`

  margin-bottom: 1.5rem;
  
  h2 {
    margin-top: 1.5rem;
    margin-bottom: 0.5rem;
    font-size: 1.25rem;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.textColor};
  }
  
  h3 {
    margin-top: 1.25rem;
    margin-bottom: 0.5rem;
    font-size: 1.125rem;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.textColor};
  }
  
  .step-content {
    margin-left: 0.5rem;
  }
  
  hr {
    visibility: hidden;
    height: 1px;
    margin: 0;
    padding: 0;
    border: none;
  }
`;

// Styled MUI Accordion to maintain style consistency
const StyledAccordion = styled(Accordion)`
  margin-bottom: 1rem !important;
  background-color: transparent !important;
  box-shadow: none !important;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px !important;
  
  &::before {
    display: none !important;
  }
  
  .MuiAccordionSummary-root {
    min-height: 48px;
    padding: 0 16px;
    border-bottom: ${props => props.expanded ? '1px solid rgba(0, 0, 0, 0.12)' : 'none'};
  }
  
  .MuiAccordionDetails-root {
    padding: 16px;
  }
  
  .MuiTypography-root {
    font-weight: 500;
    font-size: 1rem;
  }
`;

// Code block with copy and download functionality
const CopyButtonCodeBlock = ({ value, language }) => {
  const [copyStatus, setCopyStatus] = useState("idle"); // idle, copying, copied, error
  const theme = useTheme();
  
  // Get file extension based on language for download
  const getExtension = () => {
    switch (language) {
      case "javascript": return "js";
      case "typescript": return "ts";
      case "python": return "py";
      case "java": return "java";
      case "html": return "html";
      case "css": return "css";
      case "json": return "json";
      case "bash": case "sh": return "sh";
      default: return "txt";
    }
  };

  const fileExtension = getExtension();

  const copyToClipboard = () => {
    setCopyStatus("copying");
    navigator.clipboard
      .writeText(value)
      .then(() => {
        setCopyStatus("copied");
        setTimeout(() => setCopyStatus("idle"), 2000);
      })
      .catch(() => {
        setCopyStatus("error");
        setTimeout(() => setCopyStatus("idle"), 2000);
      });
  };

  const downloadCode = () => {
    const element = document.createElement("a");
    const file = new Blob([value], { type: "text/plain" });
    element.href = URL.createObjectURL(file);
    element.download = `code.${fileExtension}`;
    document.body.appendChild(element); // Required for this to work in FireFox
    element.click();
  };

  const getStatusText = () => {
    switch (copyStatus) {
      case "copying":
        return "Copying...";
      case "copied":
        return "Copied!";
      case "error":
        return "Failed to copy";
      default:
        return "Copy";
    }
  };

  return (
    <div className="code-block-container relative rounded-md overflow-hidden">
      {/* Top bar */}
      <div className="code-block-header flex items-center justify-between px-4 py-2 bg-gray-800 text-gray-300 text-xs border-b border-gray-700">
        <div className="font-mono">{language || "code"}</div>
        <div className="flex space-x-2">
          <button
            onClick={copyToClipboard}
            className="text-gray-300 hover:text-white transition-colors focus:outline-none flex items-center space-x-1"
            aria-label="Copy code to clipboard"
          >
            <span>{getStatusText()}</span>
            {copyStatus === "copied" && <CheckIcon />}
            {copyStatus === "idle" && <CopyIcon />}
          </button>
          <button
            onClick={downloadCode}
            className="text-gray-300 hover:text-white transition-colors focus:outline-none flex items-center space-x-1"
            aria-label="Download code"
          >
            <span>Download</span>
            <DownloadIcon />
          </button>
        </div>
      </div>

      {/* Code content */}
      <Highlight
        theme={themes.okaidia}
        code={String(value).replace(/\n$/, "")}
        language={language || "json"}
      >
        {({ className, style, tokens, getLineProps, getTokenProps }) => (
          <pre
            className={`${className} p-4 m-0 text-break code-pre`}
            style={{ ...style }}
          >
            {tokens.map((line, i) => (
              <div key={i} {...getLineProps({ line })}>
                {line.map((token, key) => (
                  <span key={key} {...getTokenProps({ token })} />
                ))}
              </div>
            ))}
          </pre>
        )}
      </Highlight>
    </div>
  );
};

interface WorkflowMarkdownProps {
  content: string;
  containerWidth?: number;
}

interface Section {
  title: string;
  content: string;
  isStep?: boolean;
  stepNum?: number;
  totalSteps?: number;
}

// Helper function to normalize indentation in markdown content
const normalizeIndentation = (content: string): string => {
  if (!content || typeof content !== 'string') return content;
  
  // Split the content into lines
  const lines = content.split('\n');
  
  // Process each line
  const normalizedLines = lines.map(line => {
    // Check if line starts with exactly 4 spaces (but not more)
    if (/^    [^ ]/.test(line)) {
      // Replace 4 spaces with 2 spaces to prevent code block interpretation
      return '  ' + line.substring(4);
    }
    return line;
  });
  
  // Join the lines back together
  return normalizedLines.join('\n');
};

// Process the content to format Step X/N as markdown headers
const processStepHeaders = (content: string): string => {
  if (!content) return '';
  
  // Regular expression to find step patterns: 🔄 **Step X/N: Title**
  const stepRegex = /🔄\s+\*\*Step (\d+)\/(\d+):\s+([^*]+)\*\*/g;
  
  // Replace step patterns with proper markdown headers
  let processedContent = content.replace(stepRegex, (match, stepNum, totalSteps, title) => {
    return `## Step ${stepNum}/${totalSteps}: ${title.trim()}`;
  });
  
  // Also handle patterns like 🔄 **Step X/N** without colon
  const simpleStepRegex = /🔄\s+\*\*Step (\d+)\/(\d+)\*\*\s+([^\n]+)/g;
  processedContent = processedContent.replace(simpleStepRegex, (match, stepNum, totalSteps, title) => {
    return `## Step ${stepNum}/${totalSteps}: ${title.trim()}`;
  });
  
  return processedContent;
};

// Helper function to split content by h2 headers (step headers)
const parseSteps = (content: string): Section[] => {
  if (!content) return [];
  
  // Process the content to ensure step headers are formatted correctly
  const processedContent = processStepHeaders(content);
  
  // Split by h2 headers (## Step X/N)
  const h2Regex = /##\s+Step\s+(\d+)\/(\d+)[:\s]+([^\n]+)\n/g;
  
  // Alternative approach to matchAll for compatibility
  const matches: Array<RegExpExecArray> = [];
  let match: RegExpExecArray | null;
  
  while ((match = h2Regex.exec(processedContent)) !== null) {
    matches.push(match);
  }
  
  if (matches.length === 0) {
    // If no step headers, try looking for other common patterns
    const headerRegex = /(Starting|Initiating|Beginning)\s+([^\n]+)\n/gi;
    const headerMatches: Array<RegExpExecArray> = [];
    let headerMatch: RegExpExecArray | null;
    
    while ((headerMatch = headerRegex.exec(processedContent)) !== null) {
      headerMatches.push(headerMatch);
    }
    
    if (headerMatches.length > 0) {
      const sections: Section[] = [];
      for (let i = 0; i < headerMatches.length; i++) {
        const match = headerMatches[i];
        const title = match[0].trim();
        const startPos = match.index + match[0].length;
        const endPos = i < headerMatches.length - 1 ? headerMatches[i + 1].index : processedContent.length;
        
        const sectionContent = processedContent.substring(startPos, endPos).trim();
        sections.push({
          title,
          content: sectionContent,
          isStep: false
        });
      }
      return sections;
    }
    
    // Fall back to returning whole content
    return [{
      title: 'Results',
      content: processedContent,
      isStep: false
    }];
  }
  
  const sections: Section[] = [];
  
  // For each step header match, extract the section that follows it
  for (let i = 0; i < matches.length; i++) {
    const match = matches[i];
    const stepNum = match[1];
    const totalSteps = match[2];
    const title = match[3].trim();
    const startPos = match.index;
    const endPos = i < matches.length - 1 ? matches[i + 1].index : processedContent.length;
    
    const sectionContent = processedContent.substring(startPos, endPos).trim();
    sections.push({
      title: `Step ${stepNum}/${totalSteps}: ${title}`,
      content: sectionContent,
      isStep: true,
      stepNum: parseInt(stepNum, 10),
      totalSteps: parseInt(totalSteps, 10)
    });
  }
  
  return sections;
};

export default function WorkflowMarkdown({
  content,
  containerWidth
}: WorkflowMarkdownProps) {
  /**
   * Capture situations where the codeblock tries to bleed over the screen's boundary
   * Lets peg this to the width of the parent container of the Messages Component (Index.tsx)
   */
  let customMaxWidth = containerWidth ? `${containerWidth.toString()}px` : "";
  
  // Normalize the indentation in the content
  const normalizedContent = normalizeIndentation(content);
  
  // Parse the content into sections based on step headers
  const steps = parseSteps(normalizedContent);
  
  // Track which step sections are expanded (by default, keep all expanded when new content arrives)
  const [expandedSteps, setExpandedSteps] = useState<Record<string, boolean>>({});
  
  // Create function to generate markdown components with proper styling for headers and code blocks
  const createMarkdownComponents = () => ({
    table: (props) => {
      return <table>{props.children}</table>;
    },
    pre: (props) => {
      return <div {...props}>{props.children}</div>;
    },
    code({ className, children, ...props }) {
      const match = /language-(\w+)/.exec(className || "");
      const language = match ? match[1] : null;
      
      // Special handling for markdown language - render as normal markdown content
      if (language === "markdown") {
        // Use a non-recursive approach to avoid TypeScript errors
        const simpleMarkdownComponents = {
          table: (props) => <table>{props.children}</table>,
          pre: (props) => <div {...props}>{props.children}</div>
        };
        
        return (
          <Markdown
            className="markdown bg-transparent"
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={simpleMarkdownComponents}
          >
            {String(children)}
          </Markdown>
        );
      }

      if (match) {
        return (
          <CopyButtonCodeBlock
            value={String(children).replace(/\n$/, "")}
            language={language}
          />
        );
      } else {
        if (String(children).length > 150) {
          return (
            <CopyButtonCodeBlock
              value={String(children)}
              language={"text"}
            />
          );
        } else {
          return (
            <code className={`${className}`} {...props}>
              {children}
            </code>
          );
        }
      }
    },
    a: ({ node, children, href, ...props }) => {
      const isExternal = href?.startsWith("http");

      return (
        <a
          href={href}
          onClick={(e) => {
            e.preventDefault();
            if (isExternal && window.confirm("You are about to visit an external website. Do you want to continue?")) {
              window.open(href, "_blank", "noopener,noreferrer");
            }
          }}
          {...props}
          className={`inline-flex items-center ${isExternal ? "external-link" : ""}`}
          rel={isExternal ? "noopener noreferrer" : undefined}
          aria-label={isExternal ? `${children} (opens in new tab)` : undefined}
        >
          {children}
        </a>
      );
    },
    h2: ({ node, children, ...props }) => {
      // Don't render the step header in the content since we use it in the accordion summary
      if (String(children).includes('Step ') && String(children).includes('/')) {
        return null;
      }
      // Special styling for other h2 headers
      return (
        <h2 className="step-header" {...props}>
          {children}
        </h2>
      );
    },
    h3: ({ node, children, ...props }) => {
      // Special styling for sub-headers
      return (
        <h3 className="step-subheader" {...props}>
          {children}
        </h3>
      );
    }
  });
  
  // Initialize the components with our factory function
  const markdownComponents = {
    table: (props) => <table>{props.children}</table>,
    pre: (props) => <div {...props}>{props.children}</div>,
    code: createMarkdownComponents().code,
    a: createMarkdownComponents().a,
    h2: createMarkdownComponents().h2,
    h3: createMarkdownComponents().h3
  };
  
  // Effect to initialize expanded state for all steps
  useEffect(() => {
    // Update expanded state for all steps
    const newExpandedState = { ...expandedSteps };
    let hasNewSteps = false;
    
    steps.forEach((step, index) => {
      const stepKey = `step-${index}`;
      
      // If we haven't seen this step before, set it to expanded by default
      if (expandedSteps[stepKey] === undefined) {
        newExpandedState[stepKey] = true; // Default to expanded
        hasNewSteps = true;
      }
    });
    
    if (hasNewSteps) {
      setExpandedSteps(newExpandedState);
    }
  }, [steps]);
  
  // Handle accordion expansion state changes
  const handleAccordionChange = (stepKey: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedSteps(prev => ({
      ...prev,
      [stepKey]: isExpanded
    }));
  };
  
  return (
    <MarkdownContainer>
      <div className="text-sm font-normal leading-loose" style={{ maxWidth: customMaxWidth }}>
        {steps.length === 0 ? (
          <Markdown
            className="markdown bg-transparent"
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={markdownComponents}
          >
            {normalizedContent}
          </Markdown>
        ) : (
          <div>
            {steps.map((step, index) => {
              const stepKey = `step-${index}`;
              // Default to expanded for new steps until state is initialized
              const isExpanded = expandedSteps[stepKey] !== undefined ? expandedSteps[stepKey] : true;
              
              return (
                <StyledAccordion 
                  key={stepKey}
                  expanded={isExpanded}
                  onChange={handleAccordionChange(stepKey)}
                >
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`${stepKey}-content`}
                    id={`${stepKey}-header`}
                  >
                    <Typography style={{ display: 'flex', alignItems: 'center' }}>{step.title}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <StepSection>
                      <Markdown
                        className="markdown bg-transparent"
                        remarkPlugins={[remarkGfm]}
                        rehypePlugins={[rehypeRaw]}
                        components={markdownComponents}
                      >
                        {step.content}
                      </Markdown>
                    </StepSection>
                  </AccordionDetails>
                </StyledAccordion>
              );
            })}
          </div>
        )}
      </div>
    </MarkdownContainer>
  );
}